import React, { useState, useEffect } from 'react';
import AddAccountModal from './AddAccountModal';
import { toast } from 'react-toastify';
import './AllAccounts.css';
import { getAllAccounts, getAccounts, reconnectAccount as apiReconnectAccount } from '../api/accounts';
import apiClient from '../api/client';

const TRADING_SETTING_KEY = 'profitree_enableTradingViaAllAccounts';

const isEtradeAccount = (institution) => /e[-*]?trade/i.test(String(institution || ''));

const AllAccounts = ({ setModalOpen, username }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  const [enableTradingViaAllAccounts, setEnableTradingViaAllAccounts] = useState(false);
  const [tradingSettingSaving, setTradingSettingSaving] = useState(false);

  const hasEtradeAccount = accounts.some((acc) => isEtradeAccount(acc.institution));

  useEffect(() => {
    fetchAccounts();
  }, [username]);

  // Load "enable trading via all accounts" from localStorage (and optionally backend later)
  useEffect(() => {
    if (!username) return;
    try {
      const stored = localStorage.getItem(`${TRADING_SETTING_KEY}_${username}`);
      if (stored !== null) setEnableTradingViaAllAccounts(stored === 'true');
    } catch (e) {
      // ignore
    }
  }, [username]);

  const fetchAccounts = async () => {
    if (!username) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const data = await getAllAccounts();
      setAccounts(data);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Error loading accounts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleRefresh = () => {
    fetchAccounts();
  };

  const handleAddAccount = () => {
    setShowAddAccountModal(true);
  };

  const handleSnapTradeOpen = () => {
    // Open SnapTrade modal
    if (setModalOpen) {
      setModalOpen({
        open: true,
        type: "account",
      });
    }
  };

  const handleCloseModal = () => {
    setShowAddAccountModal(false);
  };

  const handleTradingToggle = async (checked) => {
    setEnableTradingViaAllAccounts(checked);
    try {
      localStorage.setItem(`${TRADING_SETTING_KEY}_${username}`, String(checked));
    } catch (e) {
      // ignore
    }
    setTradingSettingSaving(true);
    // TODO: trading-settings endpoint not in v1 API — omitted until backend adds it

    if (checked) {
      try {
        const sessionAccounts = await getAccounts();
        const etradeSessionAccounts = sessionAccounts.filter(
          (acc) => (acc.brokerageAuthorization && (isEtradeAccount(acc.accountName) || isEtradeAccount(acc.institution)))
        );
        if (etradeSessionAccounts.length === 0) {
          toast.info('No E*TRADE accounts found in this session. Link an E*TRADE account first.');
          setTradingSettingSaving(false);
          return;
        }
        let reconnectUrl = null;
        for (const acc of etradeSessionAccounts) {
          const data = await apiReconnectAccount(acc.accountId, acc.brokerageAuthorization);
          if (data?.redirectUrl) { reconnectUrl = data.redirectUrl; break; }
        }
        if (reconnectUrl && setModalOpen) {
          setModalOpen({ open: true, type: 'account', loginLink: reconnectUrl });
          toast.success('Complete the connection in the window to enable trading permissions.');
        } else if (setModalOpen) {
          setModalOpen({ open: true, type: 'account' });
          toast.success('Reconnect initiated. Complete the connection in the window to enable trading permissions.');
        }
      } catch (err) {
        console.error('Reconnect for trading failed:', err);
        toast.error(err.response?.data?.message || 'Failed to initiate reconnect. Please try again.');
      } finally {
        setTradingSettingSaving(false);
      }
    } else {
      setTradingSettingSaving(false);
    }
  };

  const handlePropertySave = async (propertyData) => {
    try {
      await apiClient.post('/api/v1/properties', propertyData);
    } catch (error) {
      console.error("Error saving property:", error);
    }
    // Refresh accounts after adding property
    fetchAccounts();
  };

  // Filter accounts based on search query
  const filteredAccounts = accounts.filter(account => {
    const searchLower = searchQuery.toLowerCase();
    return (
      account.institution.toLowerCase().includes(searchLower) ||
      account.subAccounts.some(subAccount => 
        subAccount.name.toLowerCase().includes(searchLower)
      )
    );
  });

  return (
    <div className="all-accounts-content">
      {/* Header */}
      <div className="accounts-header">
        <h2>All Accounts</h2>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={handleAddAccount}>
            + Add Account
          </button>
          <button className="btn btn-outline-secondary" onClick={handleRefresh} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="search-section">
        <div className="search-input">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search accounts..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      {/* Trading – only for E*TRADE for now */}
      {hasEtradeAccount && (
        <div className="trading-settings-section">
          <h6 className="trading-settings-title">Trading</h6>
          <div className="trading-settings-row">
            <label className="trading-settings-label" htmlFor="enable-trading-toggle">
              Enable trading via all accounts
            </label>
            <div className="trading-settings-toggle-wrap">
              <input
                type="checkbox"
                id="enable-trading-toggle"
                className="trading-settings-toggle"
                checked={enableTradingViaAllAccounts}
                onChange={(e) => handleTradingToggle(e.target.checked)}
                disabled={tradingSettingSaving}
              />
              <label className="trading-settings-toggle-label" htmlFor="enable-trading-toggle" />
            </div>
          </div>
          <p className="trading-settings-hint">Available for E*TRADE accounts. Allows placing trades across your linked accounts from ProfiTree.</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          {error}
          <button className="retry-btn" onClick={fetchAccounts}>
            Try Again
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && !error ? (
        <div className="loading">
          <div className="loading-spinner"></div>
          Loading accounts...
        </div>
      ) : (
        /* Accounts List */
        <div className="accounts-list">
          {filteredAccounts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📊</div>
              <h3>No accounts found</h3>
              <p>
                {searchQuery 
                  ? `No accounts match "${searchQuery}"` 
                  : "You don't have any accounts yet. Add your first account to get started!"
                }
              </p>
              {!searchQuery && (
                <button className="btn btn-primary" onClick={handleAddAccount}>
                  + Add Your First Account
                </button>
              )}
            </div>
          ) : (
            filteredAccounts.map((account) => (
              <div key={account.id} className="account-group">
                <div className="account-header">
                  <div className="account-info">
                    <h3>{account.institution}</h3>
                    <span className={`status ${account.status.toLowerCase()}`}>
                      {account.status}
                    </span>
                    <span className="last-updated">
                      updated {account.lastUpdated}
                    </span>
                  </div>
                  <div className="account-actions">
                    <button className="action-btn">⋮</button>
                  </div>
                </div>
                
                <div className="sub-accounts">
                  {account.subAccounts.map((subAccount, index) => (
                    <div key={index} className="sub-account">
                      <div className="sub-account-info">
                        <span className="sub-account-name">{subAccount.name}</span>
                        <span className="sub-account-amount">
                          {formatCurrency(subAccount.amount)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Add Account Modal */}
      <AddAccountModal username={username}
        isOpen={showAddAccountModal}
        onClose={handleCloseModal}
        onSnapTradeOpen={handleSnapTradeOpen}
        onPropertySave={handlePropertySave}
      />
    </div>
  );
};

export default AllAccounts;
